import { useEffect, useState } from 'react';

import { CMS_API_URL } from '../config/apiBase';
import useSchoolSelection from './useSchoolSelection';

const EMPTY = { applied: false, variations: [], packages: [], shop: null, school: null };

/**
 * The price list of the visitor's school shop, for cards shown OUTSIDE the shop
 * (services/shopCardPrices.js overlays it). Follows the stored school / class / shop
 * selection and refetches when it changes.
 *
 * @returns {{identified: boolean, loading: boolean, applied: boolean,
 *            variations: Array, packages: Array, shop: string|null}}
 *   `identified` — the visitor has a school; `applied` — its open shop's prices are loaded.
 */
export default function useShopCardPrices(brandSlug) {
  const { schoolSlug, className, shopSlug } = useSchoolSelection(brandSlug);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(schoolSlug));

  useEffect(() => {
    if (!brandSlug || !schoolSlug) {
      setData(EMPTY);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams();
    if (className) params.set('class', className);
    if (shopSlug) params.set('shop', shopSlug);
    const query = params.toString();
    fetch(
      `${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}/${encodeURIComponent(schoolSlug)}/card-prices${query ? `?${query}` : ''}`,
      { signal: controller.signal },
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        setData(json?.applied
          ? { applied: true, variations: json.variations ?? [], packages: json.packages ?? [], shop: json.shop ?? null, school: json.school ?? schoolSlug }
          : EMPTY);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // Prices are an enhancement: on failure the cards keep their default prices.
        setData(EMPTY);
        setLoading(false);
      });
    return () => controller.abort();
  }, [brandSlug, schoolSlug, className, shopSlug]);

  return { identified: Boolean(schoolSlug), loading, ...data };
}
